import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash-es';
/* Models */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { XHeaders } from 'client/data/models/x-headers';
/* Utils */
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { isEligible } from 'site-modules/shared/utils/financing/financing';
/* Components */
import { LoadingShimmer } from 'site-modules/shared/components/loading-shimmer/loading-shimmer';
import { Link } from 'site-modules/shared/components/link/link';
/* Constants */
import {
  EXCLUDES_TAXES_AND_FEES,
  INCLUDES_TAXES_AND_FEES,
  FINANCING_DISCLAIMER,
} from 'client/site-modules/shared/constants/financing/constants';
import { EventToolbox } from 'client/utils/event-toolbox';
import { DEFAULT_DOWN_PAYMENT } from 'site-modules/shared/constants/financing/financing';
import { FINANCING_EVENTS } from 'site-modules/shared/constants/financing/financing-events';
import { TrackingConstant } from 'client/tracking/constant';

export function CapitalOneEntryPointPrequalifiedUI({
  isCapOne = false,
  isFinancingError = false,
  financingOffer = {},
  isLoading = false,
  isCounterTriggered = false,
  vinData = {},
  xTraceId = '',
  isBol = false,
}) {
  const [trackingFired, setTrackingFired] = useState(false);

  const subtitleText = isCounterTriggered ? 'Please adjust your terms to see payments for this vehicle.' : '';
  const subtitle = isFinancingError
    ? 'There was a problem calculating your payment and our team has been notified. Please try again later.'
    : subtitleText;
  const content = {
    TITLE:
      isFinancingError || !isCapOne
        ? 'You’re prequalified by Capital One'
        : 'Your prequalified payment from Capital One',
    SUBTITLE: isCapOne
      ? subtitle
      : 'This vehicle is not eligible to show your loan payment. Contact dealer to learn about your financing options.',
  };
  const zipCode = get(financingOffer, 'zipCode');
  const monthlyPayment = get(financingOffer, 'monthlyPayment', 0);
  const apr = get(financingOffer, 'buyRate');
  const term = get(financingOffer, 'loanTerm');
  const downPayment = get(financingOffer, 'downPayment', DEFAULT_DOWN_PAYMENT);
  const type = get(vinData, 'type', '').toLowerCase();
  const URL = `/financing/${vinData.vin}/payments`;
  const styleId = get(vinData, 'vehicleInfo.styleInfo.styleId');

  useEffect(
    () => {
      const isEligibleVIN = isEligible(vinData);
      const creativeId = `used-summary_financing-${isCapOne ? '' : 'un'}matched-vin-postprequal`;

      if (isCapOne && isEligibleVIN) {
        if (!isLoading && !trackingFired) {
          const isPaymenCalculated = !isFinancingError && !isCounterTriggered;

          EventToolbox.fireCustomEvent(FINANCING_EVENTS.LIST_PIXEL_POSTPREQUAL, {
            creativeId,
            payment: { monthlyPayments: monthlyPayment, aprs: apr || 0, terms: term || 60 },
          });
          EventToolbox.fireCustomEvent(FINANCING_EVENTS.CALCULATE_PAYMENT_POSTPREQUAL, {
            subactionName: `${TrackingConstant.CALCULATE_PAYMENT}_${isPaymenCalculated ? 'success' : 'failure'}`,
            creativeId,
            value: isPaymenCalculated
              ? 'success'
              : `${get(financingOffer, 'status')}. ${get(financingOffer, 'message')} ${xTraceId}`,
          });
          // Intentional guard update preventing duplicate tracking events.
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setTrackingFired(true);
        }
      } else if (!trackingFired) {
        EventToolbox.fireCustomEvent(FINANCING_EVENTS.CALCULATE_PAYMENT_POSTPREQUAL, {
          subactionName: `${TrackingConstant.CALCULATE_PAYMENT}_ineligible`,
          creativeId,
          value: `${!isCapOne ? 'unmatched dealer' : ''} ${!isEligibleVIN ? 'ineligible VIN' : ''}`,
        });
        setTrackingFired(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading]
  );

  const editBtn = (
    <div className="align-self-center">
      <Link
        to={URL}
        className="edit-btn d-flex justify-content-center align-items-center bg-primary-darker"
        data-tracking-id="financing_cta"
        data-tracking-value={content.CTA_TEXT}
        style={{ borderRadius: '50%', width: '28px', height: '28px' }}
        aria-label={content.CTA_TEXT}
      >
        <span className="icon-pencil text-white size-14" aria-hidden />
      </Link>
    </div>
  );

  return (
    <div
      className="capital-one-container text-start pt-0_75 pb-1 mt-1_5 bg-white prequalified pos-r"
      style={{
        padding: '16px 22px',
        marginBottom: isBol ? '0px' : '-24px',
        marginLeft: '-24px',
        marginRight: '-24px',
        zIndex: 1,
      }}
      data-tracking-parent={`${type}-summary_financing-${isCapOne ? 'matched' : 'unmatched'}-vin`}
    >
      <div className="size-16 fw-bold mt-0_75">{content.TITLE}</div>

      {content.SUBTITLE && (
        <div className={classNames({ 'd-flex mt-1': isCounterTriggered })}>
          <div
            className={classNames('mt-0_75', {
              'size-14 ': !isCapOne,
              'me-0_75': isCounterTriggered,
            })}
          >
            {content.SUBTITLE}
          </div>
          {isCounterTriggered && editBtn}
        </div>
      )}
      {isCapOne && (
        <div className="mt-0_5">
          {isLoading && (
            <>
              <LoadingShimmer className="mb-0_5" size={30} />
              <LoadingShimmer className="mb-0_5" />
              <LoadingShimmer size={30} />
            </>
          )}
          {!isLoading && (
            <>
              {isFinancingError && <div className="size-12">Reference #: {xTraceId}</div>}
              {!isFinancingError && !isCounterTriggered && (
                <>
                  <div className="d-flex justify-content-between">
                    <div>
                      <div className="size-30 payment">
                        {formatPriceString(monthlyPayment, 2, 2)}
                        <span className="size-20">/mo</span>
                      </div>
                      <div className="size-14">
                        {apr}% APR
                        <span className="size-16 px-0_5 fw-bold">&#183;</span>
                        {term} mo
                        <span className="size-16 px-0_5 fw-bold">&#183;</span>
                        {downPayment && formatPriceString(downPayment)} Down
                      </div>
                      <div className="size-14">
                        {zipCode && styleId ? INCLUDES_TAXES_AND_FEES : EXCLUDES_TAXES_AND_FEES}
                      </div>
                    </div>
                    {editBtn}
                  </div>

                  <hr className="my-0_75" />
                  <div className="size-12 text-cool-gray-50">{FINANCING_DISCLAIMER}</div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

CapitalOneEntryPointPrequalifiedUI.propTypes = {
  isCapOne: PropTypes.bool,
  financingOffer: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
  isFinancingError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isCounterTriggered: PropTypes.bool,
  vinData: PropTypes.shape({}),
  xTraceId: PropTypes.string,
  isBol: PropTypes.bool,
};

const stateToPropsConfig = {
  xTraceId: bindToPath('x-trace-id', XHeaders),
};

export const CapitalOneEntryPointPrequalified = connectToModel(CapitalOneEntryPointPrequalifiedUI, stateToPropsConfig);
